import React from 'react'
import { css } from '@emotion/css'
import { FormattedMessage } from 'react-intl'
import { theme } from '../../styled'
import { WithTooltip } from '../../design-system'
import clipboardCopy from 'clipboard-copy'
import { metadataCss } from '../../legacy-design-system/text'
import { SuperAdminRoleSwitch } from '../roles/role-switch'
import { RoleSwitch } from '../roles'
import { RoleSwitchProps } from '@contrast-security-inc/base-types'
import { Link } from '@contrast-security-inc/design-system-typography'
import { theme as skeletorTheme } from '@contrast-security-inc/design-system-foundations'

const tooltipTitleCss = css`
  font-weight: ${theme.fontWeights.secondary.bold};
  font-size: 12px;
  font-family: ${theme.fontFamilies.secondary};
`

const tooltipValueCss = css`
  padding-bottom: 4px;
  font-size: 12px;
  font-family: ${theme.fontFamilies.secondary};
`

const tooltipCopyLinkCss = css`
  font-size: 12px;

  color: ${skeletorTheme.color.turquoise50} !important;
  &:link,
  &:visited,
  &:hover,
  &:focus {
    color: ${skeletorTheme.color.turquoise50};
  }
`

interface TooltipProps {
  readonly serviceKey: string
  readonly onCopyServiceKey: () => void
}

interface Props<TRole extends string> extends TooltipProps {
  readonly role: TRole
}

function Label() {
  return (
    <FormattedMessage id="title_apiOnly_access" defaultMessage="API Only">
      {(text) => <span className={metadataCss}>{text}</span>}
    </FormattedMessage>
  )
}

function ApiOnlyLabelWithTooltip({
  serviceKey,
  onCopyServiceKey
}: TooltipProps) {
  return (
    <WithTooltip
      placement="bottom"
      width="auto"
      renderTooltipContent={() => (
        <div>
          <FormattedMessage id="title_serviceKey" defaultMessage="Service Key">
            {(text) => <div className={tooltipTitleCss}>{text}</div>}
          </FormattedMessage>

          <div className={tooltipValueCss} children={serviceKey} />

          <FormattedMessage id="action_copy" defaultMessage="Copy">
            {(text) => (
              <Link
                className={tooltipCopyLinkCss}
                onClick={() => copyToClipboard(serviceKey, onCopyServiceKey)}
              >
                {text}
              </Link>
            )}
          </FormattedMessage>
        </div>
      )}
      children={() => <Label />}
    />
  )
}

function getApiOnlyLabel<TRole extends string>(
  Switch: (props: RoleSwitchProps<TRole>) => React.ReactElement<any>,
  requiredRole: TRole
) {
  return function ApiOnlyLabel({
    role,
    serviceKey,
    onCopyServiceKey
  }: Props<TRole>) {
    return (
      <Switch
        role={role}
        requiredRole={requiredRole}
        renderAllowed={() => (
          <ApiOnlyLabelWithTooltip
            serviceKey={serviceKey}
            onCopyServiceKey={onCopyServiceKey}
          />
        )}
        renderDenied={() => <Label />}
      />
    )
  }
}

export function copyToClipboard(
  serviceKey: string,
  onCopyServiceKey: () => void
) {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  clipboardCopy(serviceKey)
  onCopyServiceKey()
}

export const ApiOnlyLabel = getApiOnlyLabel(RoleSwitch, 'ADMIN')
export const SuperAdminApiOnlyLabel = getApiOnlyLabel(
  SuperAdminRoleSwitch,
  'SYSTEM_ADMIN'
)
